import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle2, ChevronDown, ChevronUp, Clock, Copy, Download, Mail, XCircle, Users, FileArchive } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  pending_review: { label: "Pending", color: "bg-amber-100 text-amber-800", icon: Clock },
  approved:       { label: "Approved", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  rejected:       { label: "Rejected", color: "bg-slate-100 text-slate-600", icon: XCircle },
};

const TIER_LABEL = {
  efin_100_plus:    "EFIN · 100+ funded · 90/10",
  efin_under_100:   "EFIN · <100 funded · 80/20",
  ptin_2yr_50_plus: "PTIN 2yr · 50+ · 70/30",
  ptin_2yr_under_50:"PTIN 2yr · <50 · 60/40",
  ptin_first_year:  "PTIN 1st year · 60/40",
};

export default function AdminAffiliateContracts() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const [welcomeFor, setWelcomeFor] = useState(null);

  const load = () => {
    api.get("/affiliate/admin/contracts").then(r => setRows(r.data.contracts || []))
      .catch(() => toast.error("Could not load contracts"));
  };
  useEffect(load, []);

  const patch = async (cid, status) => {
    try {
      const r = await api.patch(`/affiliate/admin/contracts/${cid}`, { status });
      toast.success(`Marked ${status}`);
      if (status === "approved" && r.data.contract?.paperwork_code) {
        setWelcomeFor(r.data.contract);
      }
      load();
    } catch { toast.error("Update failed"); }
  };

  const openWelcome = (row) => setWelcomeFor(row);

  const downloadPdf = async (row) => {
    try {
      const r = await api.get(`/affiliate/admin/contracts/${row.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `DyNamicTax-Affiliate-Contract-${(row.full_name || "affiliate").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  if (!rows) return <Layout><div className="p-10 text-muted-foreground">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 lg:p-12" data-testid="admin-affiliate-page">
        <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft size={14} /> Back to admin</Link>
        <div className="flex items-center gap-3 mb-2">
          <Users size={22} className="text-primary" />
          <div className="label-caps">2026 season</div>
        </div>
        <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-2">Affiliate contracts.</h1>
        <p className="text-muted-foreground mb-4">{rows.length} signed contract{rows.length === 1 ? "" : "s"} on file.</p>
        <div className="mb-8">
          <Link to="/admin/affiliate-paperwork" className="inline-flex items-center gap-2 text-sm text-primary underline hover:text-primary/80" data-testid="admin-view-paperwork-link">
            <FileArchive size={14} /> View weekly paperwork uploads →
          </Link>
        </div>

        {rows.length === 0 ? (
          <Card className="p-10 text-center text-muted-foreground">
            No signed contracts yet. Share <span className="font-mono">/affiliate/apply</span> with your prospects.
          </Card>
        ) : (
          <div className="space-y-3" data-testid="affiliate-rows">
            {rows.map(r => {
              const meta = STATUS_META[r.status] || STATUS_META.pending_review;
              const Icon = meta.icon;
              const isOpen = open === r.id;
              return (
                <Card key={r.id} className="p-5" data-testid={`aff-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-4 cursor-pointer" onClick={() => setOpen(isOpen ? null : r.id)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${meta.color}`}>
                          <Icon size={10} /> {meta.label}
                        </span>
                        <span className="label-caps text-[10px] text-muted-foreground">{TIER_LABEL[r.tier] || r.tier}</span>
                      </div>
                      <div className="font-serif text-xl truncate">{r.full_name}</div>
                      <div className="text-xs text-muted-foreground truncate">{r.email} · {r.phone} · Signed {new Date(r.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-muted-foreground">{isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="mt-5 pt-5 border-t border-border grid md:grid-cols-2 gap-4 text-sm">
                      <Detail k="PTIN" v={r.ptin} />
                      <Detail k="EFIN" v={r.efin || "—"} />
                      <Detail k="Address" v={`${r.address}, ${r.city} ${r.state} ${r.zip}`.replace(/^, |,\s*$/g, "")} />
                      <Detail k="Backup phone" v={r.secondary_phone || "—"} />
                      <Detail k="Estimated returns" v={r.estimated_returns || "—"} />
                      <Detail k="Payment" v={`${r.payment_method || "—"}${r.payment_handle ? ` · ${r.payment_handle}` : ""}`} />
                      <Detail k="Signature" v={r.signature} />
                      <Detail k="Signed date" v={r.signed_date} />
                      <Detail k="Initials" v={`splits:${r.initials_splits} · pay:${r.initials_payment} · docs:${r.initials_paperwork} · fraud:${r.initials_fraud}`} />
                      <div className="md:col-span-2">
                        <div className="label-caps text-[10px] mb-1">Uploads</div>
                        <div className="space-y-1 text-xs font-mono">
                          {Object.entries(r.uploads || {}).flatMap(([k, arr]) => (arr || []).map(p => (
                            <div key={p} className="truncate"><span className="text-primary uppercase mr-2">{k}</span>…{p.slice(-40)}</div>
                          )))}
                          {!Object.keys(r.uploads || {}).length && <div className="text-muted-foreground">No uploads.</div>}
                        </div>
                      </div>
                      <div className="md:col-span-2 flex flex-wrap gap-2 pt-2">
                        <Button size="sm" onClick={() => patch(r.id, "approved")} disabled={r.status === "approved"} data-testid={`aff-approve-${r.id}`}>Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => downloadPdf(r)} data-testid={`aff-pdf-${r.id}`}>
                          <Download size={12} className="mr-1" /> Download signed PDF
                        </Button>
                        {r.status === "approved" && r.paperwork_code && (
                          <Button size="sm" variant="outline" onClick={() => openWelcome(r)} data-testid={`aff-welcome-${r.id}`}>
                            <Mail size={12} className="mr-1" /> Welcome email
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => patch(r.id, "rejected")} disabled={r.status === "rejected"} data-testid={`aff-reject-${r.id}`}>Reject</Button>
                        <Button size="sm" variant="ghost" onClick={() => patch(r.id, "pending_review")} disabled={r.status === "pending_review"}>Reopen</Button>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Welcome-email modal — copy-paste to Gmail. */}
        {welcomeFor && (
          <WelcomeEmailModal row={welcomeFor} onClose={() => setWelcomeFor(null)} />
        )}
      </div>
    </Layout>
  );
}

function WelcomeEmailModal({ row, onClose }) {
  const paperworkUrl = `${window.location.origin}/affiliate/paperwork`;
  const subject = `Welcome to the DyNamic Tax team, ${row.full_name.split(" ")[0]}! 🎉`;
  const body = `Hi ${row.full_name.split(" ")[0]},

You're officially in — welcome to the DyNamic Tax team for the 2026 tax season!

Your paperwork is on file, your contract is signed, and you're ready to prep taxes under our EFIN.

Here's what's next:

1. TAXSLAYER PRO CREDENTIALS
   Log in at https://taxslayerpro.com with:
   - Username: [FILL IN]
   - Password: [FILL IN]
   - Office code: [FILL IN]

2. YOUR WEEKLY PAPERWORK DROPBOX
   Every week, upload the required paperwork here so we can process funded returns and get you paid on time:
   ${paperworkUrl}

   Your unique access:
   - Email:  ${row.email}
   - Code:   ${row.paperwork_code}

   Bookmark this page — you'll use it every week.

3. BANK PRODUCTS
   Refund Advantage bank products (including cash advances) are already enabled on your account. Nothing else to do.

4. MENTORSHIP
   Text me anytime with questions — that's what mentorship is for.

Remember: you get paid the larger portion of each split as soon as returns fund. Missed weekly paperwork = temporary software lockout, so stay on top of it.

Excited to have you on board.

— Di'Meka
DyNamic Tax`;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const mailtoHref = `mailto:${encodeURIComponent(row.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="aff-welcome-modal">
      <div className="max-w-2xl w-full bg-card rounded-lg border border-border shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-border flex items-start justify-between gap-4">
          <div>
            <div className="label-caps text-primary mb-1">Send welcome email</div>
            <div className="font-serif text-2xl">Onboard {row.full_name.split(" ")[0]}.</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close">
            <XCircle size={20} />
          </button>
        </div>
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="rounded-md bg-primary/5 border border-primary/30 p-4 text-xs space-y-1">
            <div><span className="label-caps text-[10px] text-primary mr-2">To</span>{row.email}</div>
            <div><span className="label-caps text-[10px] text-primary mr-2">Paperwork code</span><span className="font-mono">{row.paperwork_code}</span></div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="label-caps text-[10px]">Subject</div>
              <button onClick={() => copy(subject, "Subject")} className="text-[11px] text-primary inline-flex items-center gap-1"><Copy size={11} /> Copy</button>
            </div>
            <div className="text-sm border border-border rounded px-3 py-2 bg-secondary/40">{subject}</div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="label-caps text-[10px]">Body</div>
              <button onClick={() => copy(body, "Email body")} className="text-[11px] text-primary inline-flex items-center gap-1"><Copy size={11} /> Copy</button>
            </div>
            <textarea readOnly value={body} className="w-full h-72 text-xs border border-border rounded p-3 bg-secondary/40 font-mono resize-none" data-testid="aff-welcome-body" />
          </div>
          <div className="text-[11px] text-muted-foreground">
            Fill in TaxSlayer Pro credentials before sending. Click <em>Open in email app</em> to pop it into Gmail / Apple Mail / Outlook pre-addressed.
          </div>
        </div>
        <div className="p-4 border-t border-border flex flex-wrap gap-2 justify-end bg-secondary/30">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button asChild data-testid="aff-welcome-mailto">
            <a href={mailtoHref}><Mail size={14} className="mr-2" /> Open in email app</a>
          </Button>
        </div>
      </div>
    </div>
  );
}

const Detail = ({ k, v }) => (
  <div>
    <div className="label-caps text-[10px]">{k}</div>
    <div className="font-mono text-xs break-words">{v || "—"}</div>
  </div>
);
